import { useContext, useLayoutEffect, useRef } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { DataEventProps, ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';

import { RovoSection } from '../components/rovo-section';
import { GlobalNavProductRecommendations } from '../global-nav-product-recommendations/components/global-nav-product-recommendations';
import { NavProductRecommendationsContext } from '../global-nav-product-recommendations/context/nav-product-recommendations-context';
import { UseGlobalNavProductRecommendationsResponse } from '../global-nav-product-recommendations/hooks/use-global-nav-product-recommendations';

import { Divider } from './icons/divider';
import { MobileNavCard, MobileNavCardProps } from './mobile-nav-card';

const topLinksContainerCss = css({
	paddingBlockEnd: '24px',
});

const labelCss = css({
	color: wacToken('color.primary.black'),
	textTransform: 'uppercase',
	fontFamily: 'Charlie Text',
	fontSize: '13px',
});

const pageLabelCss = css({
	paddingBlock: token('space.150'),
	lineHeight: '1.667',
});

const sectionLabelCss = css({
	paddingBlock: '28px 8px',
	lineHeight: '1.528',
	fontWeight: '500',
});

const bottomContainerCss = css({
	backgroundColor: wacToken('color.neutral.light200'),
	margin: `0 ${token('space.negative.400')} -40px ${token('space.negative.400')}`,
	padding: `0 ${token('space.400')} ${token('space.500')} ${token('space.400')}`,
});

const linkContainerCss = css({
	paddingBlock: token('space.200'),
	fontFamily: 'Charlie Text',
	fontSize: '14px',
	fontWeight: '700',
	lineHeight: '1.428',
});

export type SubnavLinkProps = {
	key: string;
	href: string;
	text: string;
	productKey?: ProductKeyTypes;
};

const SubnavLink = ({ href, text, productKey = 'home' }: Omit<SubnavLinkProps, 'key'>) => {
	return (
		<div css={linkContainerCss}>
			<Link
				type="anchor"
				href={href}
				text={text}
				aria-label={text}
				dataEventProps={
					{
						'data-product-key': productKey,
						'data-event-component': 'linkButton',
						'data-event-container': 'globalNavWacDropdownContainer',
						'data-event': 'clicked',
					} as DataEventProps
				}
			/>
		</div>
	);
};

export type LinkSection = {
	key: string;
	name: string;
	links: SubnavLinkProps[];
};

type MobileNavMenuPageProps = {
	pageLabel: string;
	cards: MobileNavCardProps[];
	linksSections?: LinkSection[];
	productKey?: ProductKeyTypes;
};

export const MobileNavMenuPage = ({
	pageLabel,
	cards,
	linksSections,
	productKey = 'home',
}: MobileNavMenuPageProps) => {
	const {
		shouldShowGlobalNavProductRecommendations,
		mobileNavPageLabelFeaturedProducts,
		fireGlobalNavProductRecoEventsWhenInView,
	} = useContext<UseGlobalNavProductRecommendationsResponse>(NavProductRecommendationsContext);
	const containerRef = useRef<HTMLDivElement>(null);

	// Observe the container to fire the event screen when it's in view
	useLayoutEffect(() => {
		// Check if IntersectionObserver is supported
		if (typeof IntersectionObserver === 'undefined') {
			return;
		}

		if (pageLabel !== mobileNavPageLabelFeaturedProducts) {
			return;
		}

		const currentContainer = containerRef.current;

		// Check if container is currently in view
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					fireGlobalNavProductRecoEventsWhenInView({
						isMobile: true,
					});
				}
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.1,
			},
		);

		if (currentContainer) {
			observer.observe(currentContainer);
		}

		return () => {
			if (currentContainer) {
				observer.unobserve(currentContainer);
			}
		};
	}, [fireGlobalNavProductRecoEventsWhenInView, mobileNavPageLabelFeaturedProducts, pageLabel]);

	return (
		<>
			<div ref={containerRef} css={topLinksContainerCss}>
				<div css={[labelCss, pageLabelCss]}>
					<span>{pageLabel}</span>
				</div>
				<div>
					{pageLabel === mobileNavPageLabelFeaturedProducts &&
					shouldShowGlobalNavProductRecommendations ? (
						<GlobalNavProductRecommendations isMobile />
					) : (
						cards.map(({ key, ...rest }) => (
							<MobileNavCard key={key} productKey={productKey} {...rest} />
						))
					)}
				</div>
				<RovoSection />
			</div>
			{linksSections && (
				<div css={bottomContainerCss}>
					{linksSections?.map(({ key, name, links }, numSection) => (
						<div key={`section-${key}`}>
							{numSection !== 0 && <Divider />}
							<div css={[labelCss, sectionLabelCss]}>
								<span>{name}</span>
							</div>
							{links.map(({ key, ...rest }) => (
								<SubnavLink key={key} productKey={productKey} {...rest} />
							))}
						</div>
					))}
				</div>
			)}
		</>
	);
};
